import React, {MutableRefObject, useEffect, useState} from 'react';
import {SidebarContent} from '../SidebarContent';
import {Button, ButtonToolbar, Label, NoData, SelectBox, VF} from '@mc/ui-v4';
import {Form, FormRenderProps} from 'react-final-form';
import {mcPrint} from '@mc/react-ui-icons';
import {OnChange} from 'react-final-form-listeners';
import {rffFormSubmit, WaitForQueryData} from '@mc/react-app-v4';
import {useQuery} from 'react-query';
import {getPrintModesItems, PrintModes} from '../../../enums/PrintModes';
import {MapEditorActions, PrintConfigType} from '@mc/react-mapio';
import {CACHE_KEY_PRINT_CONFIG} from '../../../queryKeys';
import {PrintApi, PrintConfigVO} from '../../../api';

type FieldProps = {
    name: string;
    value: string | number;
};
type Props = {onClose: () => void; mapRef: MutableRefObject<MapEditorActions>; optimalScale: FieldProps};

export const PrintLayersSidebar: React.FC<Props> = ({onClose, mapRef, optimalScale}) => {
    const modes = getPrintModesItems();
    const [mode, setMode] = useState(modes[0]);
    const [isLegendChecked, setLegendChecked] = useState(false);
    const [printConfig, setPrintConfig] = useState<PrintConfigVO>(null);

    const printConfigQuery = useQuery<PrintConfigVO>([CACHE_KEY_PRINT_CONFIG], async () => (await PrintApi.getPrintConfig()).data, {
        staleTime: 5 * 60 * 1000, // Například 5 minut
    });
    useEffect(() => {
        if (printConfigQuery.data) {
            setPrintConfig(printConfigQuery.data);
            mapRef.current.setPrintConfig(printConfigQuery.data as PrintConfigType);
        }
    }, [printConfigQuery.data]);

    return (
        <SidebarContent icon={mcPrint} title={'Tisk výřezu'} onClose={onClose}>
            <WaitForQueryData queries={[printConfigQuery]}>
                {() => {
                    if (!printConfig) {
                        return <NoData>Konfigurace tisku se nenačetla</NoData>;
                    }
                    const defaultScale = optimalScale?.value || printConfig.scales.find(scale => scale.value === 1500).value || printConfig?.scales[0]?.value;
                    return (
                        <Form
                            initialValues={{
                                orientation: printConfig?.orientations[0]?.value,
                                size: printConfig?.sizes[0]?.value,
                                multiPageScale: defaultScale,
                                format: printConfig?.formats[0]?.value,
                            }}
                            onSubmit={async data => {
                                return await rffFormSubmit(async () => {
                                    let submitData = {...data};

                                    mapRef.current?.setStartPrintProcess(true);
                                    /*TODO SUBMIT - viz PrintDirective*/
                                    if (mode.id === PrintModes.AUTO) {
                                        delete submitData.multiPageScale;
                                    }
                                    onClose();
                                });
                            }}
                        >
                            {({submitting, handleSubmit, form: {change}}: FormRenderProps) => {
                                return (
                                    <div className={'pb-2 px-2'}>
                                        <VF.SelectBox
                                            getItemId={(item: FieldProps) => item?.value}
                                            name={'orientation'}
                                            label={'Orientace'}
                                            items={printConfig.orientations}
                                            onChange={val => (change(val.target.name, val.target.value), mapRef.current.setPrintOptions(val.target))}
                                            disabled={submitting}
                                            useIdAsValue
                                        />
                                        <VF.SelectBox
                                            getItemId={(item: FieldProps) => item?.value}
                                            name={'size'}
                                            label={'Velikost'}
                                            items={printConfig.sizes}
                                            onChange={val => (change(val.target.name, val.target.value), mapRef.current.setPrintOptions(val.target))}
                                            disabled={submitting}
                                            useIdAsValue
                                        />
                                        <VF.SelectBox
                                            getItemId={(item: FieldProps) => item?.value}
                                            name={'multiPageScale'}
                                            label={'Měřítko'}
                                            items={printConfig.scales}
                                            onChange={val => (change(val.target.name, val.target.value), mapRef.current.setPrintOptions(val.target))}
                                            disabled={submitting}
                                            useIdAsValue
                                        />
                                        <VF.SelectBox
                                            getItemId={(item: FieldProps) => item?.value}
                                            name={'format'}
                                            label={'Formát'}
                                            items={printConfig.formats}
                                            onChange={val => (change(val.target.name, val.target.value), mapRef.current.setPrintOptions(val.target))}
                                            disabled={submitting}
                                            useIdAsValue
                                        />
                                        <Label>Režim</Label>
                                        <SelectBox
                                            value={mode}
                                            items={modes}
                                            disabled={submitting}
                                            className={'mb-1'}
                                            onChange={i => {
                                                let mutateMode = {value: i.id, name: i.name};
                                                setMode(mutateMode as any);
                                                mapRef.current.setPrintMode(mutateMode);
                                            }}
                                        />
                                        <VF.String
                                            name={'header'}
                                            label={'Nadpis'}
                                            disabled={submitting}
                                            onChange={val => (change('header', val), mapRef.current.setPrintOptions({name: 'header', value: val}))}
                                        />
                                        <VF.Checkbox name={'legend'} label={'Legenda'} disabled={submitting} />
                                        {/* @ts-ignore onClick na VF.Checkbox se nevyvolá při kliknutí na label, proto řešeno takto */}
                                        <OnChange name="legend">
                                            {value => {
                                                setLegendChecked(!isLegendChecked);
                                                mapRef.current.setPrintOptions({name: 'legend', value: value});
                                            }}
                                        </OnChange>
                                        {/*multiprint? debugEnabled?*/}
                                        <ButtonToolbar>
                                            <Button
                                                style={'default'}
                                                onClick={() => {
                                                    onClose();
                                                    mapRef.current.activateToolWithType(null);
                                                }}
                                                disabled={submitting}
                                            >
                                                Zrušit
                                            </Button>
                                            <Button style={'primary'} onClick={handleSubmit} disabled={submitting}>
                                                Tisk
                                            </Button>
                                        </ButtonToolbar>
                                    </div>
                                );
                            }}
                        </Form>
                    );
                }}
            </WaitForQueryData>
        </SidebarContent>
    );
};
